import {
    HarvestFormType,
    HarvestFormWorkerType,
    HarvestItemFormType
} from "components/entities/agriculture/work/HarvestFormFields";
import { uuid } from "config/app-constants";
import { IdName } from "types/common-types";
import { HarvestFormDataQuery, HarvestTemplateFormDataQuery } from "types/graphql";
import { PersonType } from "types/person-types";
import { SeasonType } from "types/season-type";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import { numberOrUndefined } from "utilities/input-utilities";

type InputType = HarvestFormDataQuery['harvestAgricultureWork'] | HarvestTemplateFormDataQuery['harvestAgricultureWorkTemplate'];

export const getHarvestFormData = (input: InputType, isEdit: boolean, defaultValue?: HarvestFormType) : HarvestFormType | undefined => {
    if(!input) return undefined;

    let workers = input.workers.items?.map(c => {
        return {
            id: isEdit ? c!.id : uuid(),
            price: c!.price,
            person: c!.person! as PersonType
        } as HarvestFormWorkerType
    });

    if(!workers?.length) workers = [...defaultValue?.workers || [{id: uuid()}]];

    let harvests = input.harvests?.items?.map(c => {
        return {
            id: isEdit ? c!.id : uuid(),
            fruitGroup: c.harvestFruitGroups?.items?.map(hg => {
                return {
                    id: isEdit ? hg.id : uuid(),
                    name: hg.fruitGroup.name,
                    classificationId: hg.fruitGroup.id
                }
            }) || [],
            fruitCategories: c.harvestFruitCategories?.items?.map(hc => {
                return {
                    id: isEdit ? hc.id : uuid(),
                    name: hc.fruitCategory.name,
                    classificationId: hc.fruitCategory.id
                }
            }) || [],
            cartQuantity: c.cartQuantity,
            fruit: c.fruit as IdName | undefined,
            kg: c.kg,
            hideFromMultiUpdate: "hideFromMultiUpdate" in c ? c.hideFromMultiUpdate : false
        }
    }) as HarvestItemFormType[];

    if(!harvests?.length) harvests = [...defaultValue?.harvests || [{id: uuid()}]] as HarvestItemFormType[];

    return {
        id: isEdit ? input.id : 0,
        name: isEdit ? input.name : defaultValue?.name,
        note: input.note || defaultValue?.note,
        date: isEdit && input.startDate ? new Date(input.startDate) : defaultValue?.date,
        season: isEdit ? (input.season as SeasonType) : defaultValue?.season,
        workers: workers,
        harvests: harvests,
    }
}

export const getHarvestSaveForm = <T> (model: HarvestFormType) : T => {
    if(model == null) return {} as T;

    let saveModel = {
        harvests: model.harvests?.map(h => {
            return {
                workId: model.id || 0,
                cartQuantity: numberOrUndefined(h.cartQuantity!),
                fruitId: h.fruit?.id,
                id: Number(h.id) || 0,
                kg: numberOrUndefined(h.kg),
                hideFromMultiUpdate: h.hideFromMultiUpdate || false,
                harvestFruitGroups: h.fruitGroup?.map(c => {
                    return {
                        fruitGroupId: Number(c.classificationId),
                        harvestId: Number(h.id) || 0,
                        id: Number(c.id) || 0
                    }
                }),
                harvestFruitCategories: h.fruitCategories?.map(c => {
                    return {
                        fruitCategoryId: Number(c.classificationId),
                        harvestId: Number(h.id) || 0,
                        id: Number(c.id) || 0
                    }
                })
            }
        }),
        id: model.id || 0,
        name: model.name!,
        seasonId: model.season?.id,
        startDate: DateUtil.toIsoDate(model.date!),
        note: model.note,
        workers: model.workers.map(w => {
            const worker = {
                date: DateUtil.format(model.date!, DateFormats.DATE),
                id: Number(w.id) ? Number(w.id) : 0,
                personId: w.person?.id,
                price: w.price !== '' ? Number(w.price) : undefined,
                workId: model.id || 0
            };

            return worker;
        })
    };

    return saveModel as T;
}
